import is from '@sindresorhus/is';
import { z } from 'zod';

import {
    createUrlTileCodec,
    type ParsedUrlTile,
    URL_TILE_SEARCH_PARAM,
    type UrlTilePayload,
    urlTileStringCodec,
    urlTileVersionNumberCodec,
} from '../url-tiles';
import { positionManagementRoute } from './route';
import { ARCHITECTURE_ID_SEARCH_PARAM } from './search-param';

export const POSITION_MANAGEMENT_POSITION_TILE_TYPE = 'v1.position-management.position' as const;
export const POSITION_MANAGEMENT_POSITION_TILE_SLOT_KEY = 'position-management.position';
export const POSITION_MANAGEMENT_CHANGELOG_TILE_TYPE = 'v1.position-management.changelog' as const;
export const POSITION_MANAGEMENT_CHANGELOG_TILE_SLOT_KEY = 'position-management.changelog';
export const POSITION_MANAGEMENT_PROPOSAL_COMPARE_TILE_TYPE = 'v1.position-management.proposal-compare' as const;
export const POSITION_MANAGEMENT_COMPARE_TILE_TYPE = 'v1.position-management.compare' as const;
export const POSITION_MANAGEMENT_COMPARE_TILE_SLOT_KEY = 'position-management.compare';
export const POSITION_MANAGEMENT_TASK_TILE_TYPE = 'v1.position-management.task' as const;
export const POSITION_MANAGEMENT_TASK_TILE_SLOT_KEY = 'position-management.task';

export const positionTileSchema = z.object({
    positionId: z.uuid(),
    preferredLanguage: z.string().optional(),
    type: z.literal(POSITION_MANAGEMENT_POSITION_TILE_TYPE),
    version: z.number().int().positive().optional(),
});

export type PositionTilePayload = z.infer<typeof positionTileSchema>;

export const positionTileCodec = createUrlTileCodec<PositionTilePayload>({
    options: [{ codec: urlTileStringCodec, field: 'preferredLanguage', key: 'lang' }],
    segments: ['positionId', { codec: urlTileVersionNumberCodec, field: 'version', optional: true }],
    type: POSITION_MANAGEMENT_POSITION_TILE_TYPE,
});

export const positionChangelogTileSchema = z.object({
    positionId: z.uuid(),
    preferredLanguage: z.string().optional(),
    type: z.literal(POSITION_MANAGEMENT_CHANGELOG_TILE_TYPE),
});

export type PositionChangelogTilePayload = z.infer<typeof positionChangelogTileSchema>;

export const positionChangelogTileCodec = createUrlTileCodec<PositionChangelogTilePayload>({
    options: [{ codec: urlTileStringCodec, field: 'preferredLanguage', key: 'lang' }],
    segments: ['positionId'],
    type: POSITION_MANAGEMENT_CHANGELOG_TILE_TYPE,
});

export const positionCompareTileSchema = z.object({
    positionId: z.uuid(),
    type: z.literal(POSITION_MANAGEMENT_COMPARE_TILE_TYPE),
    versionA: z.number().int().positive(),
    versionB: z.number().int().positive(),
});

export type PositionCompareTilePayload = z.infer<typeof positionCompareTileSchema>;

export const positionCompareTileCodec = createUrlTileCodec<PositionCompareTilePayload>({
    options: [],
    segments: [
        'positionId',
        { codec: urlTileVersionNumberCodec, field: 'versionA' },
        { codec: urlTileVersionNumberCodec, field: 'versionB' },
    ],
    type: POSITION_MANAGEMENT_COMPARE_TILE_TYPE,
});

export const createPositionCompareTilePayload = ({
    positionId,
    versionA,
    versionB,
}: Pick<PositionCompareTilePayload, 'positionId' | 'versionA' | 'versionB'>): PositionCompareTilePayload => ({
    positionId,
    type: POSITION_MANAGEMENT_COMPARE_TILE_TYPE,
    versionA,
    versionB,
});

export const positionProposalCompareTileSchema = z.object({
    positionId: z.uuid(),
    proposalBlockId: z.uuid(),
    type: z.literal(POSITION_MANAGEMENT_PROPOSAL_COMPARE_TILE_TYPE),
});
export type PositionProposalCompareTilePayload = z.infer<typeof positionProposalCompareTileSchema>;
export const positionProposalCompareTileCodec = createUrlTileCodec<PositionProposalCompareTilePayload>({
    options: [],
    segments: ['positionId', 'proposalBlockId'],
    type: POSITION_MANAGEMENT_PROPOSAL_COMPARE_TILE_TYPE,
});
export const createPositionProposalCompareTilePayload = ({
    positionId,
    proposalBlockId,
}: Omit<PositionProposalCompareTilePayload, 'type'>): PositionProposalCompareTilePayload => ({
    positionId,
    proposalBlockId,
    type: POSITION_MANAGEMENT_PROPOSAL_COMPARE_TILE_TYPE,
});

export const positionTileKey = (positionId: string): string =>
    `${POSITION_MANAGEMENT_POSITION_TILE_SLOT_KEY}.${positionId}`;

export const positionChangelogTileKey = (positionId: string): string =>
    `${POSITION_MANAGEMENT_CHANGELOG_TILE_SLOT_KEY}.${positionId}`;

export const createPositionChangelogTilePayload = ({
    positionId,
    preferredLanguage,
}: {
    readonly positionId: string;
    readonly preferredLanguage?: string;
}): PositionChangelogTilePayload => ({
    positionId,
    preferredLanguage,
    type: POSITION_MANAGEMENT_CHANGELOG_TILE_TYPE,
});

export const createPositionTilePayload = ({
    positionId,
    preferredLanguage,
    version,
}: {
    readonly positionId: string;
    readonly preferredLanguage?: string;
    readonly version?: number;
}): PositionTilePayload => ({
    positionId,
    preferredLanguage,
    type: POSITION_MANAGEMENT_POSITION_TILE_TYPE,
    version,
});

export const positionTilePayloadAtVersion = ({
    openTiles,
    positionId,
    preferredLanguage,
    version,
}: {
    readonly openTiles: readonly ParsedUrlTile[];
    readonly positionId: string;
    readonly preferredLanguage: string | undefined;
    readonly version: number;
}): PositionTilePayload => {
    const openPosition = openPositionTilePayload(openTiles, positionId);

    return openPosition === undefined
        ? createPositionTilePayload({ positionId, preferredLanguage, version })
        : { ...openPosition, version };
};

export const openPositionTilePayload = (
    openTiles: readonly ParsedUrlTile[],
    positionId: string,
): PositionTilePayload | undefined =>
    positionTileSchema.safeParse(openTiles.find(({ tileKey }) => tileKey === positionTileKey(positionId))?.payload)
        .data;

const POSITION_MANAGEMENT_TILE_TYPES: ReadonlySet<string> = new Set([
    POSITION_MANAGEMENT_POSITION_TILE_TYPE,
    POSITION_MANAGEMENT_CHANGELOG_TILE_TYPE,
    POSITION_MANAGEMENT_COMPARE_TILE_TYPE,
    POSITION_MANAGEMENT_PROPOSAL_COMPARE_TILE_TYPE,
]);

export const isPositionManagementTile = (payload: UrlTilePayload): boolean =>
    POSITION_MANAGEMENT_TILE_TYPES.has(payload.type);

export const selectPositionIdFromTile = (payload: UrlTilePayload): string | undefined =>
    is.string(payload['positionId']) ? payload['positionId'] : undefined;

export const positionTilePath = (payload: PositionTilePayload, architectureId: string): string => {
    const searchParams = new URLSearchParams({
        [ARCHITECTURE_ID_SEARCH_PARAM]: architectureId,
        [URL_TILE_SEARCH_PARAM]: positionTileCodec.serialize(payload),
    });

    return `${positionManagementRoute.patterns.positionManagement}?${searchParams.toString()}`;
};

/** A task of a Position's edit draft: a new one while [taskId] is absent, else the version [version] of [taskId]. */
export const positionTaskTileSchema = z.object({
    language: z.string().optional(),
    positionId: z.uuid(),
    taskId: z.string().trim().min(1).optional(),
    type: z.literal(POSITION_MANAGEMENT_TASK_TILE_TYPE),
    version: z.number().int().positive().optional(),
});

export type PositionTaskTilePayload = z.infer<typeof positionTaskTileSchema>;

export const positionTaskTileCodec = createUrlTileCodec<PositionTaskTilePayload>({
    options: [
        { codec: urlTileStringCodec, field: 'language', key: 'lang' },
        { codec: urlTileStringCodec, field: 'taskId', key: 'task' },
        { codec: urlTileVersionNumberCodec, field: 'version', key: 'v' },
    ],
    segments: ['positionId'],
    type: POSITION_MANAGEMENT_TASK_TILE_TYPE,
});

export const createPositionTaskTilePayload = ({
    language,
    positionId,
    taskId,
    version,
}: {
    readonly language?: string;
    readonly positionId: string;
    readonly taskId?: string;
    readonly version?: number;
}): PositionTaskTilePayload => ({
    ...(language === undefined ? {} : { language }),
    positionId,
    ...(taskId === undefined ? {} : { taskId }),
    type: POSITION_MANAGEMENT_TASK_TILE_TYPE,
    ...(version === undefined ? {} : { version }),
});

export const isPositionTaskTilePayload = (payload: unknown): payload is PositionTaskTilePayload =>
    positionTaskTileSchema.safeParse(payload).success;
